import React, { useEffect, useState, useRef } from 'react';
import { ButtonStylized, ButtonStylizedProps } from '../ButtonStylized/ButtonStylized';
import './ButtonStylizedFillable.css';

export enum FillDirection {
  /** Fill from left to right */
  LEFT_TO_RIGHT = 'left-to-right',
  /** Fill from right to left */
  RIGHT_TO_LEFT = 'right-to-left',
}

export interface ButtonStylizedFillableProps extends ButtonStylizedProps {
  /** Direction of the fill animation */
  fillDirection: FillDirection;
  
  /** Duration of the fill animation in milliseconds */
  fillDurationMs: number;
  
  /** Background color of the button */
  backgroundColor?: string;
  
  /** Color to use for the fill overlay */
  fillColor?: string;
  
  /** Whether to show the glow/flash animation */
  showGlowEffect?: boolean;
  
  /** Callback when fill animation completes */
  onFillComplete?: () => void;
}

/**
 * ButtonStylizedFillable - An enhanced ButtonStylized with animated fill/unfill effect
 * 
 * Supports:
 * - FILL: Starts empty, fills from left to right over duration
 * - UNFILL: Starts full, empties from left to right over duration
 * - Optional glow/pulse effect during animation
 * - Callback when animation completes
 */
export const ButtonStylizedFillable: React.FC<ButtonStylizedFillableProps> = ({
  fillDirection,
  fillDurationMs,
  backgroundColor,
  fillColor,
  showGlowEffect = false,
  onFillComplete,
  additionalStyles = {},
  ...buttonProps
}) => {
  const [elapsedMs, setElapsedMs] = useState(0);
  const [animationComplete, setAnimationComplete] = useState(false);
  const startTimeRef = useRef<number>(Date.now());
  
  // Track elapsed time for progress calculation
  useEffect(() => {
    // Use the ref to maintain consistent start time across re-renders
    const startTime = startTimeRef.current;
    
    const updateElapsed = () => {
      const now = Date.now();
      const elapsed = now - startTime;
      setElapsedMs(elapsed);
      
      if (elapsed >= fillDurationMs) {
        setAnimationComplete(true);
        if (onFillComplete) {
          onFillComplete();
        }
      }
    };
    
    // Update every 50ms for smooth animation
    updateElapsed();
    const interval = setInterval(updateElapsed, 50);
    
    return () => clearInterval(interval);
  }, [fillDurationMs, onFillComplete]);
  
  // Calculate fill percentage based on direction
  const progressPercent = Math.min((elapsedMs / fillDurationMs) * 100, 100);
  
  const isAnimating = !animationComplete;
  
  // Merge additional styles with fill effect
  const mergedStyles = {
    position: 'relative' as const,
    overflow: 'hidden' as const,
    ...(backgroundColor ? { backgroundColor } : {}),
    ...additionalStyles,
  };
  
  return (
    <div 
      className={`button-stylized-fillable-wrapper ${showGlowEffect && isAnimating ? 'glow-effect' : ''}`}
      style={{ position: 'relative', display: 'inline-block', width: additionalStyles.width || 'auto' }}
    >
      <ButtonStylized
        {...buttonProps}
        additionalStyles={mergedStyles}
      >
        {/* Fill overlay */}
        {isAnimating && (
          <div
            className="fill-overlay"
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              height: '100%',
              width: '100%',
              backgroundColor: fillColor || 'currentColor',
              opacity: 0.3,
              clipPath: fillDirection === FillDirection.LEFT_TO_RIGHT
                ? `inset(0 ${100 - progressPercent}% 0 0)`  // Left to right: clip from right
                : `inset(0 0 0 ${100 - progressPercent}%)`, // Right to left: clip from left
              transition: 'clip-path 0.05s linear',
              pointerEvents: 'none',
              zIndex: 0,
            }}
          />
        )}
        
        {/* Button content (ensure it's above overlay) */}
        <div style={{ position: 'relative', zIndex: 1 }}>
          {buttonProps.children}
        </div>
      </ButtonStylized>
    </div>
  );
};
